import { Controller } from "@hotwired/stimulus"
import { loadConnectAndInitialize } from "@stripe/connect-js"

// Mounts Stripe's embedded `account-onboarding` component inline. On exit we
// navigate to the result page, whose controller re-syncs Connect status (same
// post-onboarding behavior as the hosted #complete action).
export default class extends Controller {
  static values = {
    publishableKey: String,
    accountSessionUrl: String,
    resultUrl: String
  }

  static targets = ["container", "loader", "error"]

  connect() {
    this.fetchClientSecret = this.fetchClientSecret.bind(this)
    this.initConnect()
  }

  async fetchClientSecret() {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    const response = await fetch(this.accountSessionUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken || "",
        "Accept": "application/json"
      },
      credentials: "same-origin"
    })

    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.error || "Failed to create account session")
    }

    const { client_secret: clientSecret } = await response.json()
    return clientSecret
  }

  async initConnect() {
    try {
      const stripeConnectInstance = loadConnectAndInitialize({
        publishableKey: this.publishableKeyValue,
        fetchClientSecret: this.fetchClientSecret
      })

      const onboardingComponent = stripeConnectInstance.create("account-onboarding")

      if (this.hasLoaderTarget) {
        onboardingComponent.setOnLoaderStart(() => {
          this.loaderTarget.hidden = true
        })
      }

      if (this.hasErrorTarget) {
        onboardingComponent.setOnLoadError((loadError) => {
          this.errorTarget.textContent = loadError.error?.message || "Failed to load onboarding"
          this.errorTarget.hidden = false
        })
      }

      onboardingComponent.setOnExit(() => {
        if (this.hasResultUrlValue) {
          window.location.href = this.resultUrlValue
        }
      })

      this.containerTarget.appendChild(onboardingComponent)
    } catch (err) {
      if (this.hasErrorTarget) {
        this.errorTarget.textContent = err.message || "Something went wrong"
        this.errorTarget.hidden = false
      }
      if (this.hasLoaderTarget) {
        this.loaderTarget.hidden = true
      }
    }
  }
}
